/**
 * BottleneckAnalysis — Health check cards with severity background tints.
 */

import { useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { useBottleneck } from '@/lib/api/hooks/useAnalytics';
import type { FunnelParams, Severity, DataQuality } from '@/types/analytics';

interface BottleneckAnalysisProps {
  dateFrom?: string;
  dateTo?: string;
  campaignId?: string;
  source?: string;
  highlightId?: string;
}

const BG_TINT: Record<Severity, string> = {
  green:  'bg-green-50/50 dark:bg-green-950/30',
  yellow: 'bg-amber-50/50 dark:bg-amber-950/30',
  red:    'bg-red-50/50 dark:bg-red-950/30',
};

const SEVERITY_LABEL: Record<Severity, string> = {
  green:  'Healthy',
  yellow: 'Watch',
  red:    'Action needed',
};

const DOT_COLOR: Record<Severity, string> = {
  green:  'bg-green-500',
  yellow: 'bg-amber-500',
  red:    'bg-red-500',
};

function MetricCard({
  id,
  title,
  value,
  subtitle,
  severity,
  dataQuality,
}: {
  id?: string;
  title: string;
  value: string;
  subtitle?: string;
  severity: Severity;
  dataQuality?: DataQuality;
}) {
  return (
    <Card id={id} className={`${BG_TINT[severity]} min-h-[120px]`}>
      <CardHeader className="pb-1 pt-4 px-4">
        <CardTitle className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
          {title}
        </CardTitle>
      </CardHeader>
      <CardContent className="pb-4 px-4">
        <p className="text-3xl font-bold leading-tight">{value}</p>
        {subtitle && <p className="text-xs text-muted-foreground mt-1">{subtitle}</p>}
        <div className="flex items-center gap-1.5 mt-2">
          <span className={`h-1.5 w-1.5 rounded-full ${DOT_COLOR[severity]}`} />
          <span className="text-xs text-muted-foreground">{SEVERITY_LABEL[severity]}</span>
        </div>
      </CardContent>
    </Card>
  );
}

const STAGE_LABELS: Record<string, string> = {
  scheduled: 'Lead → Scheduled',
  showed:    'Scheduled → Showed',
  qualified: 'Showed → Qualified',
  proposal:  'Qualified → Proposal',
  closed_won:'Proposal → Closed',
};

const TRANSITION_LABELS: Record<string, string> = {
  lead_to_scheduled:   'Lead → Scheduled',
  scheduled_to_showed: 'Scheduled → Showed',
  showed_to_qualified: 'Showed → Qualified',
  qualified_to_closed: 'Qualified → Closed',
};

export function BottleneckAnalysis({ dateFrom, dateTo, campaignId, source, highlightId }: BottleneckAnalysisProps) {
  const params: FunnelParams = {
    date_from: dateFrom,
    date_to: dateTo,
    campaign_id: campaignId,
    source,
  };

  const { data, isLoading, error } = useBottleneck(params);

  useEffect(() => {
    if (!highlightId || isLoading) return;
    const timer = setTimeout(() => {
      const el = document.getElementById(`section-${highlightId}`);
      if (!el) return;
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      el.classList.add('ring-2', 'ring-blue-500', 'ring-offset-2');
      setTimeout(() => el.classList.remove('ring-2', 'ring-blue-500', 'ring-offset-2'), 3000);
    }, 150);
    return () => clearTimeout(timer);
  }, [highlightId, isLoading]);

  if (isLoading) {
    return (
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
        {[...Array(6)].map((_, i) => (
          <Card key={i}>
            <CardHeader className="pb-1 pt-4 px-4">
              <Skeleton className="h-3 w-24" />
            </CardHeader>
            <CardContent className="pb-4 px-4">
              <Skeleton className="h-9 w-16 mb-2" />
              <Skeleton className="h-3 w-20" />
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="py-12 text-center text-sm text-muted-foreground">
        {error ? `Failed to load health data: ${(error as Error).message}` : 'No data available for this period.'}
      </div>
    );
  }

  const ttfc = data.time_to_first_call;
  const ttfcValue = ttfc.avg_minutes >= 60
    ? `${(ttfc.avg_minutes / 60).toFixed(1)}h avg`
    : `${ttfc.avg_minutes}m avg`;
  const ttfcSubtitle = `Median: ${ttfc.median_minutes >= 60
    ? `${(ttfc.median_minutes / 60).toFixed(1)}h`
    : `${ttfc.median_minutes}m`}`;

  // Find worst drop-off stage
  const dropoffs = Object.entries(data.stage_dropoffs);
  const worstDropoff = dropoffs.sort((a, b) => b[1].drop_off_pct - a[1].drop_off_pct)[0];

  // Find worst stage duration
  const durations = Object.entries(data.stage_durations);
  const worstDuration = durations.sort((a, b) => b[1].avg_days - a[1].avg_days)[0];

  return (
    <div className="space-y-6">
      {/* Primary metrics */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
        <MetricCard
          id="section-speed_to_lead"
          title="Time to First Call"
          value={ttfcValue}
          subtitle={ttfcSubtitle}
          severity={ttfc.severity}
          dataQuality={ttfc.data_quality}
        />

        <MetricCard
          id="section-close_rate"
          title="Close Rate"
          value={`${data.close_rate.rate}%`}
          subtitle="Showed → Closed Won. Target: >35%"
          severity={data.close_rate.severity}
          dataQuality={data.close_rate.data_quality}
        />

        <MetricCard
          id="section-show_rate"
          title="Show Rate"
          value={`${data.show_rate.rate}%`}
          subtitle="Past meetings attended. Target: >75%"
          severity={data.show_rate.severity}
          dataQuality={data.show_rate.data_quality}
        />

        <MetricCard
          id="section-stale_leads"
          title="Stale Leads"
          value={String(data.stale_leads.count)}
          subtitle="No activity in 7+ days"
          severity={data.stale_leads.severity}
          dataQuality={data.stale_leads.data_quality}
        />

        <MetricCard
          id="section-qualification_rate"
          title="Qualification Rate"
          value={`${data.call_quality.qualification_rate.toFixed(1)}%`}
          subtitle={`${data.call_quality.qualified_held_calls}/${data.call_quality.total_held_calls} held calls qualified`}
          severity={data.call_quality.severity}
          dataQuality={data.call_quality.data_quality}
        />

        <MetricCard
          id="section-avg_duration"
          title="Avg Call Duration"
          value={`${data.call_quality.avg_duration_all.toFixed(0)} min`}
          subtitle="All held calls"
          severity={data.call_quality.severity}
          dataQuality={data.call_quality.data_quality}
        />

        {worstDropoff && (
          <MetricCard
            title={`Biggest Drop-off: ${STAGE_LABELS[worstDropoff[0]] ?? worstDropoff[0]}`}
            value={`${worstDropoff[1].drop_off_pct}%`}
            subtitle="Prospects lost at this transition"
            severity={worstDropoff[1].severity}
            dataQuality={worstDropoff[1].data_quality}
          />
        )}

        {worstDuration && worstDuration[1].avg_days > 0 && (
          <MetricCard
            title={`Slowest Stage: ${STAGE_LABELS[worstDuration[0]] ?? worstDuration[0]}`}
            value={`${worstDuration[1].avg_days}d avg`}
            subtitle="Avg time prospects spend at this stage"
            severity={worstDuration[1].severity}
            dataQuality={worstDuration[1].data_quality}
          />
        )}
      </div>

      {/* Stage drop-off breakdown */}
      <div>
        <h3 className="text-xs font-medium uppercase tracking-wide text-muted-foreground mb-3">Stage Drop-off</h3>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
          {dropoffs.map(([stageName, metric]) => (
            <Card key={stageName} className={`${BG_TINT[metric.severity]}`}>
              <CardContent className="pt-3 pb-3 px-4">
                <p className="text-[10px] font-medium uppercase tracking-wide text-muted-foreground mb-1">
                  {STAGE_LABELS[stageName] ?? stageName}
                </p>
                <p className="text-xl font-bold">{metric.drop_off_pct}%</p>
                <p className="text-xs text-muted-foreground">drop-off</p>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>

      {/* Stage duration breakdown */}
      {durations.some(([, m]) => m.avg_days > 0) && (
        <div>
          <h3 className="text-xs font-medium uppercase tracking-wide text-muted-foreground mb-3">Avg Time per Stage</h3>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
            {durations
              .filter(([, m]) => m.avg_days > 0)
              .map(([stageName, metric]) => (
                <Card key={stageName} className={`${BG_TINT[metric.severity]}`}>
                  <CardContent className="pt-3 pb-3 px-4 flex items-center justify-between">
                    <div>
                      <p className="text-[10px] font-medium uppercase tracking-wide text-muted-foreground capitalize mb-1">
                        {stageName.replace(/_/g, ' ')}
                      </p>
                      <p className="text-xl font-bold">{metric.avg_days}d</p>
                    </div>
                    {metric.data_quality !== 'complete' && (
                      <Badge variant="outline" className="text-[10px]">
                        {metric.data_quality}
                      </Badge>
                    )}
                  </CardContent>
                </Card>
              ))}
          </div>
        </div>
      )}

      {/* Deal velocity */}
      {data.deal_velocity && (
        <div id="section-deal_velocity">
          <h3 className="text-xs font-medium uppercase tracking-wide text-muted-foreground mb-3">Deal Velocity</h3>
          <div className="space-y-3">
            {/* Summary card */}
            <Card className={`${BG_TINT[data.deal_velocity.severity]}`}>
              <CardContent className="pt-4 pb-4 px-4">
                {data.deal_velocity.data_quality === 'insufficient' ? (
                  <p className="text-sm text-muted-foreground">No closed deals yet in this period.</p>
                ) : (
                  <div className="flex flex-wrap gap-6 items-center">
                    <div>
                      <p className="text-[10px] font-medium uppercase tracking-wide text-muted-foreground mb-1">Avg Lead to Close</p>
                      <p className="text-3xl font-bold">{data.deal_velocity.avg_days_lead_to_close}d</p>
                    </div>
                    <div>
                      <p className="text-[10px] font-medium uppercase tracking-wide text-muted-foreground mb-1">Median</p>
                      <p className="text-3xl font-bold">{data.deal_velocity.median_days_lead_to_close}d</p>
                    </div>
                    <div className="flex flex-col gap-1.5">
                      <div className="flex items-center gap-1.5">
                        <span className={`h-1.5 w-1.5 rounded-full ${DOT_COLOR[data.deal_velocity.severity]}`} />
                        <span className="text-xs text-muted-foreground">
                          {data.deal_velocity.severity === 'green' ? 'Fast (<14d)' : data.deal_velocity.severity === 'yellow' ? 'Moderate (14–30d)' : 'Slow (>30d)'}
                        </span>
                      </div>
                      {data.deal_velocity.data_quality !== 'complete' && (
                        <Badge variant="outline" className="text-[10px] self-start">
                          {data.deal_velocity.data_quality} data
                        </Badge>
                      )}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* By-source breakdown */}
            {Object.keys(data.deal_velocity.by_source).length > 0 && (
              <div>
                <p className="text-[10px] font-medium uppercase tracking-wide text-muted-foreground mb-2">By Source</p>
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2">
                  {Object.entries(data.deal_velocity.by_source).map(([src, m]) => (
                    <Card key={src} className="bg-muted/20">
                      <CardContent className="pt-3 pb-3 px-4">
                        <p className="text-[10px] font-medium uppercase tracking-wide text-muted-foreground capitalize mb-1">
                          {src.replace(/_/g, ' ')}
                        </p>
                        <p className="text-lg font-bold">{m.avg_days}d avg</p>
                        <p className="text-xs text-muted-foreground">
                          {m.median_days}d median · {m.count} deal{m.count !== 1 ? 's' : ''}
                        </p>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>
            )}

            {/* Stage transition breakdown */}
            {Object.values(data.deal_velocity.by_stage_transition).some(m => m.avg_days > 0) && (
              <div>
                <p className="text-[10px] font-medium uppercase tracking-wide text-muted-foreground mb-2">By Stage Transition</p>
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2">
                  {Object.entries(data.deal_velocity.by_stage_transition)
                    .filter(([, m]) => m.avg_days > 0)
                    .map(([key, m]) => (
                      <Card key={key} className="bg-muted/20">
                        <CardContent className="pt-3 pb-3 px-4">
                          <p className="text-[10px] font-medium uppercase tracking-wide text-muted-foreground mb-1">
                            {TRANSITION_LABELS[key] ?? key.replace(/_/g, ' ')}
                          </p>
                          <p className="text-lg font-bold">{m.avg_days}d avg</p>
                          <p className="text-xs text-muted-foreground">{m.median_days}d median</p>
                        </CardContent>
                      </Card>
                    ))}
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
